import { useCallback, useRef } from 'react'
import { FilePdf, Image, X, DownloadSimple } from '@phosphor-icons/react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { transformMimeType, validateFileHeader } from '../../../../../helpers/fileValidation'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import { validateAdditionalFile } from '../../helpers'

const FilesInput = ({
  label,
  columnsSize,
  handleFieldChange,
  files = [],
  accept,
  maxNumberOfFiles,
  maxFileSizeInMB,
}) => {
  const { t } = useTranslation(['eventManagement'])
  const { displaySnackbar } = useSnackbar()

  const inputRef = useRef(null)
  const acceptedFilesLabel = accept.map((ext) => ext.toUpperCase()).join(', ')

  const handleAddFile = useCallback(
    (file) => {
      const errorMessage = validateAdditionalFile(
        file,
        files.length,
        maxFileSizeInMB * 1000000,
        accept,
        maxNumberOfFiles,
        t,
      )

      if (errorMessage) {
        displaySnackbar(errorMessage, true)
        return
      }

      const reader = new FileReader()

      reader.onloadend = (e) => {
        if (e.target.readyState === FileReader.DONE) {
          const bytes = new Uint8Array(e.target.result)

          const fileType = transformMimeType(file.type)
          const isValid = validateFileHeader(bytes, fileType)

          if (isValid) {
            handleFieldChange({ target: { name: 'files', value: [...files, file] } })
            displaySnackbar(t('eventManagement:common.fileAdded', { name: file.name }))
          } else {
            displaySnackbar(
              t('eventManagement:common.fileTypeNotSupported', { types: acceptedFilesLabel }),
              true,
            )
          }
        }
      }
      reader.readAsArrayBuffer(file)
    },
    [
      files,
      acceptedFilesLabel,
      displaySnackbar,
      handleFieldChange,
      accept,
      maxFileSizeInMB,
      maxNumberOfFiles,
      t,
    ],
  )

  const handleDeleteFile = (indexToDelete) => {
    handleFieldChange({
      target: { name: 'files', value: files.filter((_file, index) => index !== indexToDelete) },
    })

    // reset input so same file can be selected again
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const renderFileIcon = (file) => {
    const fileExtension = (typeof file === 'string' ? file.split('.') : file.type.split('/'))[1]

    if (fileExtension === 'pdf') {
      return <FilePdf />
    } else {
      return <Image />
    }
  }

  return (
    <div className={`column is-${columnsSize}`}>
      <div className="event-form-pdf-input-wrapper">
        <p className="event-form-pdf-input__label">
          {label}
          <span> ({t('eventManagement:common.optional')})</span>
        </p>
        <input
          ref={inputRef}
          accept={accept.map((extension) => '.' + extension).join(', ')}
          type="file"
          hidden
          onChange={(e) => {
            const file = e.target.files[0]

            if (file) {
              handleAddFile(file)
            }

            // reset input so same file can be selected again
            e.target.value = ''
          }}
        />
        <button
          onClick={(e) => {
            inputRef.current.click()
            e.currentTarget.blur()
          }}
          className="button button-primary event-form-pdf-input__upload-button"
          type="button"
          disabled={files?.length >= maxNumberOfFiles}
        >
          <DownloadSimple size={18} />
          {t('eventManagement:common.upload')}
        </button>
        <p className="event-form-pdf-input__helper-text">
          {t('eventManagement:common.acceptedFilesHelper', {
            files: acceptedFilesLabel,
            max: maxNumberOfFiles,
            size: maxFileSizeInMB,
          })}
        </p>
        {files?.map((file, index) => (
          <div className="event-form-pdf-input__file" key={`${index}-${file.name}`}>
            {renderFileIcon(file)}
            {typeof file === 'string' ? file : file.name}{' '}
            <X onClick={() => handleDeleteFile(index)} />
          </div>
        ))}
      </div>
    </div>
  )
}

FilesInput.propTypes = {
  label: PropTypes.string,
  columnsSize: PropTypes.number.isRequired,
  handleFieldChange: PropTypes.func,
  PropTypes: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  files: PropTypes.array,
  accept: PropTypes.array,
  maxNumberOfFiles: PropTypes.number,
  maxFileSize: PropTypes.number,
}

export default FilesInput
